import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { getAlert } from '../../api'
import { Skeleton } from '@/components/ui/Skeleton'
import { PriorityDot } from '../queue/PriorityDot'
import { StatusIcon } from '../queue/StatusIcon'
import { formatTimeAgo } from '@/lib/format'

interface Props {
  alertIds: readonly string[]
}

/**
 * Compact list of related alerts — typically other open alerts on the same
 * customer. Click-through opens the alert detail; the back button returns
 * here (React Router preserves history).
 */
export function RelatedAlertsList({ alertIds }: Props) {
  if (alertIds.length === 0) {
    return <p className="text-sm text-muted-fg">No related alerts on this customer.</p>
  }
  return (
    <ul className="flex flex-col gap-1">
      {alertIds.map((id) => (
        <RelatedAlertRow key={id} id={id} />
      ))}
    </ul>
  )
}

function RelatedAlertRow({ id }: { id: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['alerts', 'detail', id],
    queryFn: () => getAlert(id),
    staleTime: 30_000,
  })

  if (isLoading || !data) return <Skeleton className="h-10 w-full" />
  return (
    <li>
      <Link
        to={`/alerts/${id}`}
        className="flex items-center gap-2 rounded border border-transparent px-2 py-2 text-sm hover:border-border hover:bg-muted"
      >
        <PriorityDot priority={data.priority} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-fg">{data.scenario_name}</p>
          <p className="truncate text-xs text-muted-fg">
            <span className="font-mono">{data.alert_number}</span> · {formatTimeAgo(data.created_at)}
          </p>
        </div>
        <StatusIcon status={data.status} />
        <ChevronRight size={14} className="text-muted-fg" aria-hidden />
      </Link>
    </li>
  )
}
